import { useState, useMemo, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { differenceInSeconds } from "date-fns";
import { useQuery, useQueries } from "@tanstack/react-query";
import LayoutWide from "../layouts/LayoutWide.jsx";
import useTimezone from "../hooks/useTimezone.js";
import { formatDate, formatChartAxisLabel, formatChartTooltip, formatRelative } from "../utility/datetime.js";
import Spinner from "../components/Spinner.jsx";
import Gauge from "../components/Gauge.jsx";
import GrowPhaseBar, { PHASE_COLORS } from "../components/GrowPhaseBar.jsx";
import MetricChart from "../components/MetricChart.jsx";
import ReactECharts from "echarts-for-react";
import { computeGrowMarks, activeEventTypes, EVENT_META } from "../components/GrowPhaseOverlay.jsx";
import GrowNotesFeed from "../components/GrowNotesFeed.jsx";
import InfoTooltip from "../components/InfoTooltip.jsx";
import HarvestLog from "../components/HarvestLog.jsx";
import PhotoGallery, { Lightbox } from "../components/PhotoGallery.jsx";
import { photoScatterSeries } from "../utility/photoChart.js";
import { computeVPDSeries, computeVPD } from "../utility/vpd.js";
import { useMetricQuery } from "../hooks/useMetricQuery.js";
import { useIsStale } from "../hooks/useIsStale.js";
import { useTheme } from "../hooks/useTheme.js";
import TimeRangePicker from "../components/TimeRangePicker.jsx";
import {
  useGrowQuery,
  useGrowPhasesQuery,
  useGrowNotesQuery,
  useGrowHarvestsQuery,
  useAdvancePhase,
  useRevertPhase,
  useCompleteGrow,
  useUpdateGrow,
  useUpdateGrowPhase,
  useGrowPhaseSummariesQuery,
  useStabilityScore,
} from "../hooks/useGrowsQuery.js";
import {
  useGrowSensorsQuery,
  useSetGrowSensors,
  useGrowPhaseSensorsQuery,
  useSetGrowPhaseSensors,
} from "../hooks/useSensorAssignments.js";
import { usePhotosQuery } from "../hooks/usePhotosQuery.js";
import SensorAssignmentPicker from "../components/SensorAssignmentPicker.jsx";
import StabilityScore from "../components/StabilityScore.jsx";
import GrowReferenceToggle, { useGrowReference } from "../components/GrowReferenceToggle.jsx";
import InlineEdit from "../components/InlineEdit.jsx";
import { PencilIcon, CheckIcon, XMarkIcon } from "@heroicons/react/20/solid";
import Card from "../components/ui/Card.jsx";
import Button from "../components/ui/Button.jsx";
import Badge from "../components/ui/Badge.jsx";
import ErrorState from "../components/ui/ErrorState.jsx";
import Input from "../components/ui/Input.jsx";
import Select from "../components/ui/Select.jsx";

const API = import.meta.env.VITE_API_BASE_URL;

const PHASE_SEQUENCE = ["Inoculation", "Colonization", "Pinning", "Fruiting", "Harvesting"];

// Outcome classification for a completed grow. Values must match the DB CHECK
// constraint (migration 030) and the API's allowed set.
const GROW_OUTCOMES = [
  { value: "success",      label: "Success",      activeClass: "bg-green-100 text-green-800 border-green-300" },
  { value: "contaminated", label: "Contaminated", activeClass: "bg-red-100 text-red-700 border-red-300" },
  { value: "poor_yield",   label: "Poor yield",   activeClass: "bg-amber-100 text-amber-800 border-amber-300" },
  { value: "other",        label: "Other",        activeClass: "bg-gray-200 text-gray-700 border-gray-400" },
];

function nextPhase(currentPhaseName) {
  const idx = PHASE_SEQUENCE.indexOf(currentPhaseName);
  if (idx === -1 || idx === PHASE_SEQUENCE.length - 1) return null;
  return PHASE_SEQUENCE[idx + 1];
}

const STATUS_BADGE_COLOR = {
  active:    "success",
  completed: "gray",
  abandoned: "danger",
};

// When a grow is completed/abandoned with no recorded outcome, infer a suggested
// one from note event_types so a contaminated grow never reads identically to a
// clean completion. Purely a display hint — never written back, and grow.outcome
// (if the grower did set one) always wins.
function inferOutcome(grow, notes) {
  if (grow.outcome || grow.status === "active") return null;
  const hasContamNote = notes.some(n => n.event_type === "contam_spotted");
  return hasContamNote ? "contaminated" : null;
}

// Returns hostname + label + sensorLabels + live sensor values + thresholds
function useDashboardDevice(deviceId) {
  return useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => {
      const res = await fetch(`${API}dashboard`, { credentials: "include" });
      if (!res.ok) throw new Error("dashboard fetch failed");
      return res.json();
    },
    staleTime: 60_000,
    // Stays enabled regardless of deviceId so the query cache (shared with
    // useTeamDevices) doesn't go stale. A disabled useQuery keeps returning
    // its last cached `data` forever rather than clearing it, which left
    // `device` (and the "No device" UI below) stuck on the previously
    // assigned device after detaching one via the PATCH device_id: null flow.
    select: (data) => {
      if (!deviceId) return null;
      const d = data.devices?.[deviceId];
      if (!d) return null;
      const sensorLabels = {};
      const sensorValues = {}; // { sensor_code: { temperature: val, humidity: val, co2: val } }
      for (const s of (d.sensors ?? [])) {
        sensorLabels[s.sensor_code] = s.display_name || s.sensor_name || s.sensor_code;
        sensorValues[s.sensor_code] = {};
        for (const m of (s.metrics ?? [])) {
          sensorValues[s.sensor_code][m.metric] = m.latest_value;
        }
      }
      return {
        hostname: d.device.hostname,
        label: d.device.display_name ?? d.device.hostname,
        sensorLabels,
        sensorValues,
        lastSeen: d.device.last_seen ?? null,
        thresholds: d.thresholds?.thresholds ?? {},
        sensors: d.sensors ?? [],
      };
    },
  });
}

// Returns [{ id, label }] for every device on the team, for the grow's device
// picker. Shares the "dashboard" query cache with useDashboardDevice.
function useTeamDevices() {
  return useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => {
      const res = await fetch(`${API}dashboard`, { credentials: "include" });
      if (!res.ok) throw new Error("dashboard fetch failed");
      return res.json();
    },
    staleTime: 60_000,
    select: (data) =>
      Object.entries(data.devices ?? {}).map(([id, d]) => ({
        id: parseInt(id, 10),
        label: d.device.display_name ?? d.device.hostname,
      })),
  });
}

// Renders a VPD chart computed from the fetched temp+humidity series (no extra API call).
function VPDChart({ hostname, timeRange, marks, darkMode = false, timeZone, sensorCodes = null }) {
  const tz = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { data: tempData } = useMetricQuery({ metric: "temperature", hostname, ...timeRange });
  const { data: humData }  = useMetricQuery({ metric: "humidity",    hostname, ...timeRange });

  const vpdSeries = useMemo(() => {
    const keep = (s) => !sensorCodes || sensorCodes.has(s.sensor);
    const temps = (tempData ?? []).filter(keep);
    const hums = (humData ?? []).filter(keep);
    // Pair temp and humidity from the same sensor when possible, else first of each.
    const temp = temps.find(t => hums.some(h => h.sensor === t.sensor)) ?? temps[0];
    const hum = hums.find(h => h.sensor === temp?.sensor) ?? hums[0];
    return computeVPDSeries(temp?.points ?? [], hum?.points ?? []);
  }, [tempData, humData, sensorCodes]);

  const axisColor = darkMode ? "#4b5563" : "#6b7280";
  const gridColor = darkMode ? "#4b5563" : "#e5e7eb";
  const textColor = darkMode ? "#9ca3af" : "#374151";

  const option = useMemo(() => ({
    animation: false,
    grid: { top: 36, right: 16, bottom: 36, left: 56, containLabel: false },
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "cross", animation: false },
      formatter: (params) => {
        const ts = formatChartTooltip(params[0].axisValue, tz);
        return `${ts}<br/>VPD: <b>${params[0].data[1].toFixed(3)} kPa</b>`;
      },
    },
    xAxis: {
      type: "time",
      axisLabel: {
        formatter: (val) => formatChartAxisLabel(val, tz, timeRange.end - timeRange.start),
        hideOverlap: true,
        fontSize: 11,
        color: textColor,
      },
      axisLine: { lineStyle: { color: axisColor } },
      splitLine: { show: false },
    },
    yAxis: {
      type: "value",
      name: "kPa",
      nameLocation: "end",
      nameTextStyle: { color: textColor, fontSize: 11, padding: [0, 0, 0, -40] },
      axisLabel: { formatter: "{value}", fontSize: 11, color: textColor },
      axisLine: { lineStyle: { color: axisColor } },
      splitLine: { lineStyle: { type: "dashed", color: gridColor } },
    },
    series: [{
      name: "VPD",
      type: "line",
      smooth: true,
      symbol: "none",
      lineStyle: { color: "#0ea5e9", width: 2 },
      itemStyle: { color: "#0ea5e9" },
      areaStyle: { color: "#0ea5e9", opacity: 0.08 },
      data: vpdSeries.map(p => [p.t, p.v]),
      connectNulls: false,
      ...marks,
    }],
  }), [vpdSeries, marks, axisColor, gridColor, textColor]);

  if (!hostname) return null;

  return (
    <div className={`${darkMode ? 'bg-gray-700' : 'bg-white border border-gray-200'} rounded-lg p-4 flex flex-col gap-2`}>
      <h3 className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>VPD</h3>
      {vpdSeries.length === 0 ? (
        <div className="flex items-center justify-center h-40 text-gray-500 text-sm">No data</div>
      ) : (
        <ReactECharts
          option={option}
          style={{ height: 200 }}
          opts={{ renderer: "canvas" }}
          notMerge
          onChartReady={(chart) => { chart.group = "grow-detail"; }}
        />
      )}
    </div>
  );
}

// One sensor's row within a phase's "Phase Sensor Averages" breakdown.
// A legacy pre-migration row (sensor_id == null) renders as a muted
// "combined (pre-upgrade)" row with no reliability figures — it represents
// the old blended-average behavior and can't be retroactively split per sensor.
function SensorSummaryRow({ s }) {
  const isLegacy = s.sensor_id == null;
  const sensorLabel = isLegacy
    ? "Combined (pre-upgrade)"
    : (s.sensor_display_name || s.sensor_code || `Sensor ${s.sensor_id}`);
  const metricRows = [
    { label: "Temp", value: s.mean_temp_f, unit: "°F", min: s.min_temp_f, max: s.max_temp_f, digits: 1 },
    { label: "Humidity", value: s.mean_humidity, unit: "%", min: s.min_humidity, max: s.max_humidity, digits: 1 },
    { label: "CO₂", value: s.mean_co2_ppm, unit: " ppm", min: s.min_co2_ppm, max: s.max_co2_ppm, digits: 0 },
  ];
  return (
    <div className={isLegacy ? "opacity-60" : undefined}>
      <div className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
        {sensorLabel}
        {isLegacy && (
          <InfoTooltip label="Legacy combined row">
            Computed before per-sensor tracking shipped. This blends every sensor on the device
            into one average and can't be split apart after the fact.
          </InfoTooltip>
        )}
      </div>
      <dl className="grid grid-cols-2 sm:grid-cols-4 gap-y-1.5 gap-x-3 text-sm">
        {metricRows.map(r => (
          <div key={r.label} className="contents">
            <dt className="text-gray-400 dark:text-gray-500">{r.label}</dt>
            <dd className="text-right sm:text-left tabular-nums text-gray-700 dark:text-gray-200">
              {r.value != null ? (
                <>
                  <span className="font-medium">{r.value.toFixed(r.digits)}{r.unit}</span>{" "}
                  <span className="text-xs text-gray-400 dark:text-gray-500">
                    ({r.min?.toFixed(r.digits)}–{r.max?.toFixed(r.digits)})
                  </span>
                </>
              ) : "—"}
            </dd>
          </div>
        ))}
        <div className="contents">
          <dt className="text-gray-400 dark:text-gray-500">Samples</dt>
          <dd className="text-right sm:text-left tabular-nums text-gray-700 dark:text-gray-200">{s.sample_count}</dd>
        </div>
      </dl>
      {!isLegacy && s.uptime_pct != null && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-400 dark:text-gray-500 mt-1.5">
          <span>
            Uptime{" "}
            <span className={`font-medium tabular-nums ${s.uptime_pct < 90 ? "text-red-500 dark:text-red-400" : "text-gray-600 dark:text-gray-300"}`}>
              {s.uptime_pct.toFixed(1)}%
            </span>
          </span>
          <span>Gaps <span className="font-medium tabular-nums text-gray-600 dark:text-gray-300">{s.gap_count}</span></span>
          {s.longest_gap_seconds > 0 && (
            <span>
              Longest gap{" "}
              <span className="font-medium tabular-nums text-gray-600 dark:text-gray-300">
                {s.longest_gap_seconds < 3600
                  ? `${Math.round(s.longest_gap_seconds / 60)}m`
                  : `${(s.longest_gap_seconds / 3600).toFixed(1)}h`}
              </span>
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default function GrowDetail() {
  const [theme] = useTheme();
  const darkMode = theme === "dark";
  const tz = useTimezone();
  const { growId } = useParams();

  const { data: grow, isLoading: growLoading, isError: growError } = useGrowQuery(growId);
  const { data: phases = [], isLoading: phasesLoading } = useGrowPhasesQuery(growId);
  const { data: notes = [] } = useGrowNotesQuery(growId);
  const { data: harvests = [] } = useGrowHarvestsQuery(growId);
  const { data: phaseSummaries = [] } = useGrowPhaseSummariesQuery(growId);
  const advancePhase = useAdvancePhase(growId);
  const revertPhase = useRevertPhase(growId);
  const completeGrow = useCompleteGrow(growId);
  const updateGrow = useUpdateGrow(growId);
  const updateGrowPhase = useUpdateGrowPhase(growId);

  const { data: device, isLoading: deviceLoading } = useDashboardDevice(grow?.device_id);
  const { data: teamDevices = [] } = useTeamDevices();

  // Live gauge values patched by SSE
  const [liveValues, setLiveValues] = useState(null);
  const [liveLastSeen, setLiveLastSeen] = useState(null);
  const sseStartedRef = useRef(false);
  const growSensorRowRef = useRef(null);
  const phasePillsRef = useRef(null);
  // The SSE listener is long-lived and closes over grow.device_id, but grow loads
  // async after the stream opens. Keep the current device id in a ref so the handler
  // always matches against the latest value without re-subscribing.
  const growDeviceIdRef = useRef(null);
  useEffect(() => { growDeviceIdRef.current = grow?.device_id ?? null; }, [grow?.device_id]);

  // Merged sensor values: SSE patch wins over dashboard snapshot
  const effectiveLastSeen = liveLastSeen ?? device?.lastSeen ?? null;
  const isActiveForStaleness = grow?.status === "active";
  const liveIsStale = useIsStale(isActiveForStaleness ? effectiveLastSeen : null);

  const currentPhaseForScore = phases.find(p => !p.ended_at) ?? null;
  const stabilityScore = useStabilityScore({ hostname: device?.hostname, currentPhase: currentPhaseForScore });

  const reference = useGrowReference(growId);

  const [advanceError, setAdvanceError] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showRevertConfirm, setShowRevertConfirm] = useState(false);
  const [revertError, setRevertError] = useState(null);
  const [showComplete, setShowComplete] = useState(false);
  const [completeNote, setCompleteNote] = useState("");
  const [completeOutcome, setCompleteOutcome] = useState("");
  const [completeError, setCompleteError] = useState(null);
  const [timeRange, setTimeRange] = useState(null); // set from grow dates in useEffect
  const [enabledTypes, setEnabledTypes] = useState(null);
  const [editPhaseId, setEditPhaseId] = useState(null);
  const [phaseEditDeviceId, setPhaseEditDeviceId] = useState("");
  const [phaseEditSensorMode, setPhaseEditSensorMode] = useState("device_wide");
  const [phaseEditSensorIds, setPhaseEditSensorIds] = useState([]);
  // Existing-phase pre-hydration: the picker's initial toggle/checklist state must
  // reflect what's actually saved for this phase, not a hardcoded default. See
  // committee finding #3, infra/.planning/proposals/sensor-assignments.md.
  const { data: editPhaseSensors } = useGrowPhaseSensorsQuery(growId, editPhaseId);
  const setPhaseSensors = useSetGrowPhaseSensors(growId, editPhaseId);
  useEffect(() => {
    if (editPhaseSensors) {
      setPhaseEditSensorMode(editPhaseSensors.sensor_mode);
      setPhaseEditSensorIds(editPhaseSensors.sensor_ids ?? []);
    }
  }, [editPhaseSensors]);
  const [phaseSensorError, setPhaseSensorError] = useState(null);
  const [startedAtEdit, setStartedAtEdit] = useState(false);
  const [startedAtValue, setStartedAtValue] = useState("");
  const [startedAtError, setStartedAtError] = useState(null);
  const [deviceEdit, setDeviceEdit] = useState(false);
  const [deviceValue, setDeviceValue] = useState(""); // "" = no device
  const [deviceError, setDeviceError] = useState(null);
  const [growSensorEdit, setGrowSensorEdit] = useState(false);
  const [growSensorEditMode, setGrowSensorEditMode] = useState("device_wide");
  const [growSensorEditIds, setGrowSensorEditIds] = useState([]);
  const [growSensorError, setGrowSensorError] = useState(null);
  const setGrowSensors = useSetGrowSensors(growId);

  // Initialize time range from grow dates once grow loads
  useEffect(() => {
    if (!grow) return;
    const start = new Date(grow.started_at).getTime();
    const end = grow.ended_at ? new Date(grow.ended_at).getTime() : Date.now();
    setTimeRange({ start, end });
  }, [grow?.id]); // only re-run when grow ID changes, not on every re-render

  // SSE: patch live sensor values
  useEffect(() => {
    if (!device?.hostname || sseStartedRef.current) return;
    sseStartedRef.current = true;
    const es = new EventSource(`${API}dashboardlive`, { withCredentials: true });
    es.addEventListener("metrics", (e) => {
      const updates = JSON.parse(e.data);
      const update = updates.find(u => u.device_id === growDeviceIdRef.current);
      if (!update) return;
      if (update.last_seen) setLiveLastSeen(update.last_seen);
      const next = {};
      for (const su of update.sensors) {
        next[su.sensor_code] = {};
        for (const mu of su.metrics) {
          next[su.sensor_code][mu.metric] = mu.latest_value;
        }
      }
      setLiveValues(next);
    });
    return () => { es.close(); sseStartedRef.current = false; };
  }, [device?.hostname]);

  const presentTypes = useMemo(() => activeEventTypes(notes), [notes]);

  const enabledSet = useMemo(() => {
    if (enabledTypes === null) return new Set(presentTypes);
    return enabledTypes;
  }, [enabledTypes, presentTypes]);

  function toggleType(type) {
    const next = new Set(enabledSet);
    if (next.has(type)) next.delete(type); else next.add(type);
    setEnabledTypes(next);
  }

  const marks = useMemo(
    () => computeGrowMarks(phases, notes, enabledSet),
    [phases, notes, enabledSet]
  );

  // Photos on the chart: a scatter series of thumbnails, shown only when the "Photo"
  // event tag is enabled. Clicking a thumbnail opens the lightbox.
  const { data: photos = [] } = usePhotosQuery("grows", growId);
  const [chartPhoto, setChartPhoto] = useState(null);
  const photosOnChart = enabledSet.has("photo_taken");
  const photosInRange = useMemo(() => {
    if (!timeRange) return [];
    return photos.filter((p) => {
      const t = new Date(p.taken_at || p.created_at).getTime();
      return t >= timeRange.start && t <= timeRange.end;
    });
  }, [photos, timeRange]);
  const photoSeries = useMemo(
    () => (photosOnChart ? photoScatterSeries(photosInRange, { darkMode }) : null),
    [photosOnChart, photosInRange, darkMode]
  );
  const openChartPhoto = (photoId) => {
    const p = photos.find((x) => x.id === photoId);
    if (p) setChartPhoto(p);
  };

  // Clamp time range to grow floor/ceiling
  function handleRangeChange(range) {
    if (!grow) return setTimeRange(range);
    const floor = new Date(grow.started_at).getTime();
    const ceiling = grow.ended_at ? new Date(grow.ended_at).getTime() : Date.now();
    setTimeRange({
      start: Math.max(range.start, floor),
      end: Math.min(range.end, ceiling),
    });
  }

  // Dashboard device options for phase device selector (must be before early returns)
  const deviceOptions = useMemo(() => {
    if (!device || !grow) return [];
    return [{ id: grow?.device_id, label: device.label }];
  }, [device, grow?.device_id]);

  // Compute unique hostnames per phase for multi-device chart queries
  const phaseHostnames = useMemo(() => {
    if (!phases.length || !device) return [];
    const seen = new Set();
    if (device.hostname) seen.add(device.hostname);
    return Array.from(seen);
  }, [phases, device]);

  // Multi-device metric queries using useQueries (rules-of-hooks safe)
  const METRICS = ["temperature", "humidity", "co2"];
  const multiQueries = useQueries({
    queries: timeRange
      ? phaseHostnames.flatMap(hostname =>
          METRICS.map(metric => ({
            queryKey: ["metric", metric, hostname, timeRange.start, timeRange.end],
            queryFn: async () => {
              const params = new URLSearchParams({
                metric,
                hostname,
                start: Math.floor(timeRange.start / 1000).toString(),
                end: Math.floor(timeRange.end / 1000).toString(),
              });
              const res = await fetch(`${API}metrics/range?${params}`, { credentials: "include" });
              if (!res.ok) throw new Error(`metrics/range ${res.status}`);
              return res.json();
            },
            staleTime: 60_000,
          }))
        )
      : [],
  });

  // Live-tile filtering: mirrors the backend's phase -> grow -> device-wide fallback
  // chain (computePhaseSummary / ResolveOwnerSensors) so the gauges shown here match
  // what the grow is actually tracking, not just whatever the device reports. Keyed
  // off sensor_mode, not row count — an empty "custom" assignment list is a real,
  // intentional "tracking nothing" result, not a fallback trigger. See
  // infra/.planning/proposals/sensor-assignments.md, Frontend section.
  // Computed above the early returns below (hooks must run unconditionally every
  // render) using pre-return-safe values — phases/currentPhaseForLiveFilter is
  // already available before the loading/error early returns.
  const currentPhaseForLiveFilter = phases.find(p => !p.ended_at) ?? null;
  const { data: growSensors } = useGrowSensorsQuery(growId);
  const { data: currentPhaseSensors } = useGrowPhaseSensorsQuery(growId, currentPhaseForLiveFilter?.id);
  const effectiveSensorAssignment = useMemo(() => {
    if (currentPhaseForLiveFilter && currentPhaseForLiveFilter.sensor_mode === "custom" && currentPhaseSensors) {
      return { mode: "custom", ids: currentPhaseSensors.sensor_ids ?? [] };
    }
    if (grow?.sensor_mode === "custom" && growSensors) {
      return { mode: "custom", ids: growSensors.sensor_ids ?? [] };
    }
    return { mode: "device_wide", ids: null };
  }, [currentPhaseForLiveFilter, currentPhaseSensors, grow?.sensor_mode, growSensors]);
  const visibleSensors = useMemo(() => {
    if (!device?.sensors) return [];
    if (effectiveSensorAssignment.mode === "device_wide") return device.sensors;
    const idSet = new Set(effectiveSensorAssignment.ids);
    return device.sensors.filter(s => idSet.has(s.id));
  }, [device?.sensors, effectiveSensorAssignment]);

  // Chart series filter: null (no filtering) when the grow tracks every sensor.
  const chartSensorCodes = useMemo(
    () => (effectiveSensorAssignment.mode === "device_wide" ? null : new Set(visibleSensors.map(s => s.sensor_code))),
    [effectiveSensorAssignment.mode, visibleSensors]
  );

  if (growLoading || phasesLoading) return <LayoutWide title="Grow"><Spinner /></LayoutWide>;
  if (growError || !grow) {
    return (
      <LayoutWide title="Grow">
        <ErrorState>Grow not found.</ErrorState>
      </LayoutWide>
    );
  }

  const currentPhase = currentPhaseForLiveFilter;
  const inferredOutcome = inferOutcome(grow, notes);
  const next = currentPhase ? nextPhase(currentPhase.phase_name) : null;
  // Previous phase (the one that would be re-opened by a "move back"). Only
  // available when there is more than one phase and a current open phase.
  const orderedPhases = [...phases].sort((a, b) => a.phase_order - b.phase_order);
  const currentIdx = currentPhase ? orderedPhases.findIndex(p => p.id === currentPhase.id) : -1;
  const prevPhase = currentIdx > 0 ? orderedPhases[currentIdx - 1] : null;
  const isActive = grow.status === "active";
  const isCompleted = grow.status === "completed";

  const effectiveValues = liveValues ?? device?.sensorValues ?? {};

  async function handleComplete() {
    setCompleteError(null);
    try {
      await completeGrow.mutateAsync({ note: completeNote, outcome: completeOutcome || undefined });
      setShowComplete(false);
      setCompleteNote("");
      setCompleteOutcome("");
      setCompleteError(null);
    } catch (err) {
      setCompleteError(err.message ?? "Failed to complete grow");
    }
  }

  async function handleAdvance() {
    if (!next) return;
    setAdvanceError(null);
    try {
      await advancePhase.mutateAsync(next);
      setShowConfirm(false);
    } catch (err) {
      setAdvanceError(err.message ?? "Failed to advance phase");
    }
  }

  async function handleRevert() {
    if (!prevPhase) return;
    setRevertError(null);
    try {
      await revertPhase.mutateAsync();
      setShowRevertConfirm(false);
    } catch (err) {
      setRevertError(err.message ?? "Failed to move back a phase");
    }
  }

  async function handleSaveStartedAt() {
    setStartedAtError(null);
    const parsed = new Date(startedAtValue);
    if (isNaN(parsed.getTime())) { setStartedAtError("Invalid date"); return; }
    if (parsed > new Date()) { setStartedAtError("Cannot be in the future"); return; }
    try {
      await updateGrow.mutateAsync({ started_at: parsed.toISOString() });
      setStartedAtEdit(false);
      // Re-initialize time range floor
      setTimeRange(prev => prev ? { ...prev, start: parsed.getTime() } : prev);
    } catch (err) {
      setStartedAtError(err.message ?? "Failed to save");
    }
  }

  async function handleSavePhaseDevice(phase) {
    setPhaseSensorError(null);
    const deviceId = phaseEditDeviceId === "" ? null : parseInt(phaseEditDeviceId, 10);
    try {
      await updateGrowPhase.mutateAsync({ phaseId: phase.id, device_id: deviceId });
      await setPhaseSensors.mutateAsync({
        sensor_mode: phaseEditSensorMode,
        sensor_ids: phaseEditSensorMode === "custom" ? phaseEditSensorIds : [],
      });
      setEditPhaseId(null);
    } catch (err) {
      setPhaseSensorError(err.message ?? "Failed to save sensor selection");
    }
  }

  async function handleSaveDevice() {
    setDeviceError(null);
    const deviceId = deviceValue === "" ? null : parseInt(deviceValue, 10);
    try {
      await updateGrow.mutateAsync({ device_id: deviceId });
      setDeviceEdit(false);
    } catch (err) {
      setDeviceError(err.message ?? "Failed to save");
    }
  }

  async function handleSaveGrowSensors() {
    setGrowSensorError(null);
    try {
      await setGrowSensors.mutateAsync({
        sensor_mode: growSensorEditMode,
        sensor_ids: growSensorEditMode === "custom" ? growSensorEditIds : [],
      });
      setGrowSensorEdit(false);
    } catch (err) {
      setGrowSensorError(err.message ?? "Failed to save sensor selection");
    }
  }


  return (
    <LayoutWide title={grow.name}>
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 mb-4 text-sm text-gray-400 dark:text-gray-500">
        <Link to="/grows" className="hover:text-gray-600 dark:hover:text-gray-300 transition-colors">Grows</Link>
        <span>/</span>
        <span className="text-gray-700 dark:text-gray-300 font-medium">{grow.name}</span>
      </div>

      {/* Header card */}
      <Card padding="none" className="p-5 mb-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h1 className="text-2xl font-semibold text-sky-800 dark:text-sky-300">
                <InlineEdit
                  value={grow.name}
                  placeholder="Grow name"
                  onSave={(name) => updateGrow.mutate({ name, status: grow.status })}
                  className="text-2xl font-semibold text-sky-800 dark:text-sky-300"
                />
              </h1>
              <Badge color={STATUS_BADGE_COLOR[grow.status] ?? STATUS_BADGE_COLOR.active}>
                {grow.status}
              </Badge>
              {grow.outcome ? (() => {
                const o = GROW_OUTCOMES.find(x => x.value === grow.outcome);
                return (
                  <span className={`text-xs rounded-full px-2.5 py-0.5 font-medium border ${o?.activeClass ?? "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600"}`}>
                    {o?.label ?? grow.outcome}
                  </span>
                );
              })() : inferredOutcome && (() => {
                const o = GROW_OUTCOMES.find(x => x.value === inferredOutcome);
                return (
                  <span
                    className={`text-xs rounded-full px-2.5 py-0.5 font-medium border border-dashed ${o?.activeClass ?? "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600"}`}
                    title="Inferred from a contamination note — no outcome was recorded when this grow was completed"
                  >
                    {o?.label} (inferred)
                  </span>
                );
              })()}
            </div>
            {grow.species && (
              <span className="inline-block text-sm bg-sky-50 dark:bg-sky-950 text-sky-700 dark:text-sky-400 border border-sky-200 dark:border-sky-800 rounded-full px-3 py-0.5">
                {grow.species}
              </span>
            )}
          </div>

          {isActive && (prevPhase || next) && !showConfirm && !showRevertConfirm && (
            <div className={`grid grid-cols-1 ${prevPhase && next ? "sm:grid-cols-2" : ""} gap-2 w-full sm:w-auto sm:shrink-0`}>
              {prevPhase && (
                <Button
                  onClick={() => { setShowRevertConfirm(true); setShowConfirm(false); setShowComplete(false); }}
                  className="sm:whitespace-nowrap"
                >
                  ← Move back to {prevPhase.phase_name}
                </Button>
              )}
              {next && (
                <Button
                  onClick={() => { setShowConfirm(true); setShowComplete(false); }}
                  className="sm:whitespace-nowrap"
                >
                  Advance to {next} →
                </Button>
              )}
            </div>
          )}

          {isActive && next && showConfirm && (
            <div className="flex flex-col items-end gap-2 shrink-0">
              <div className="text-sm text-gray-600 dark:text-gray-300">
                Move from <strong>{currentPhase.phase_name}</strong> to <strong>{next}</strong>?
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleAdvance}
                  disabled={advancePhase.isPending}
                  size="sm"
                >
                  {advancePhase.isPending ? "Advancing…" : "Confirm"}
                </Button>
                <button
                  onClick={() => setShowConfirm(false)}
                  className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 px-3 py-1.5"
                >
                  Cancel
                </button>
              </div>
              {advanceError && <div className="text-xs text-red-600 dark:text-red-400">{advanceError}</div>}
            </div>
          )}

          {isActive && prevPhase && showRevertConfirm && (
            <div className="flex flex-col items-end gap-2 shrink-0">
              <div className="text-sm text-gray-600 dark:text-gray-300 max-w-xs text-right">
                Move back from <strong>{currentPhase.phase_name}</strong> to <strong>{prevPhase.phase_name}</strong>? This deletes the <strong>{currentPhase.phase_name}</strong> phase and re-opens <strong>{prevPhase.phase_name}</strong>.
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleRevert}
                  disabled={revertPhase.isPending}
                  size="sm"
                >
                  {revertPhase.isPending ? "Moving back…" : "Confirm"}
                </Button>
                <button
                  onClick={() => { setShowRevertConfirm(false); setRevertError(null); }}
                  className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 px-3 py-1.5"
                >
                  Cancel
                </button>
              </div>
              {revertError && <div className="text-xs text-red-600 dark:text-red-400">{revertError}</div>}
            </div>
          )}

          {isActive && !showComplete && (
            <Button
              variant="secondary"
              onClick={() => { setShowComplete(true); setShowConfirm(false); }}
              className="shrink-0"
            >
              Complete Grow
            </Button>
          )}

          {isActive && showComplete && (
            <div className="flex flex-col gap-2 shrink-0 max-w-sm">
              <div className="text-sm text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded px-3 py-2">
                Have you logged your final harvest? (optional but recommended)
              </div>
              <textarea
                value={completeNote}
                onChange={e => setCompleteNote(e.target.value)}
                placeholder="Completion note (required)…"
                rows={2}
                className="text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-gray-400"
              />
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">How did it turn out? (optional)</div>
                <div className="flex flex-wrap gap-1.5">
                  {GROW_OUTCOMES.map(o => (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() => setCompleteOutcome(prev => prev === o.value ? "" : o.value)}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                        completeOutcome === o.value
                          ? o.activeClass
                          : "bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-400 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleComplete}
                  disabled={!completeNote.trim() || completeGrow.isPending}
                  className="text-sm bg-gray-700 text-white rounded px-3 py-1.5 hover:bg-gray-800 transition-colors disabled:opacity-50"
                >
                  {completeGrow.isPending ? "Completing…" : "Complete Grow"}
                </button>
                <button
                  onClick={() => { setShowComplete(false); setCompleteNote(""); setCompleteOutcome(""); setCompleteError(null); }}
                  className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 px-3 py-1.5"
                >
                  Cancel
                </button>
              </div>
              {completeError && <div className="text-xs text-red-600 dark:text-red-400">{completeError}</div>}
            </div>
          )}
        </div>

        <div className="mt-4 flex items-end gap-4">
          <div className="flex-1">
            <GrowPhaseBar phases={phases} showLabel />
          </div>
          <StabilityScore score={stabilityScore} size="md" />
        </div>

        {phases.length > 0 && (
          <div ref={phasePillsRef} className="flex flex-wrap gap-3 mt-3">
            {phases.map(p => (
              <div key={p.id} className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                <span className={`w-2.5 h-2.5 rounded-sm ${PHASE_COLORS[p.phase_name] ?? "bg-gray-300"}`} />
                {p.phase_name}
                {!p.ended_at && <span className="text-sky-500 dark:text-sky-400 font-medium">(current)</span>}
                <button
                  onClick={() => {
                    setEditPhaseId(p.id);
                    setPhaseEditDeviceId(p.device_id?.toString() ?? "");
                    setPhaseSensorError(null);
                    // Reset to device_wide/empty immediately so the panel doesn't
                    // flash the PREVIOUSLY edited phase's state while this phase's
                    // GET .../sensors is in flight; the pre-hydration effect above
                    // overwrites this once the real data arrives.
                    setPhaseEditSensorMode("device_wide");
                    setPhaseEditSensorIds([]);
                  }}
                  className="opacity-40 hover:opacity-100 transition-opacity"
                  title="Edit phase device"
                >
                  <PencilIcon className="size-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Phase device edit panel */}
        {editPhaseId != null && (() => {
          const phase = phases.find(p => p.id === editPhaseId);
          if (!phase) return null;
          return (
            <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <span className="font-medium text-gray-700 dark:text-gray-200">{phase.phase_name} — device:</span>
                <Select
                  size="sm"
                  value={phaseEditDeviceId}
                  onChange={e => setPhaseEditDeviceId(e.target.value)}
                >
                  <option value="">Use grow default</option>
                  {deviceOptions.map(d => (
                    <option key={d.id} value={d.id}>{d.label}</option>
                  ))}
                </Select>
              </div>
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-200 block mb-1">Sensors to track:</span>
                <SensorAssignmentPicker
                  sensors={device?.sensors ?? []}
                  sensorMode={phaseEditSensorMode}
                  sensorIds={phaseEditSensorIds}
                  onChange={({ sensor_mode, sensor_ids }) => { setPhaseEditSensorMode(sensor_mode); setPhaseEditSensorIds(sensor_ids); }}
                />
              </div>
              {phaseSensorError && (
                <div className="text-xs text-red-600 dark:text-red-400">{phaseSensorError}</div>
              )}
              <div className="flex items-center gap-3">
                <Button
                  size="sm"
                  onClick={() => handleSavePhaseDevice(phase)}
                  disabled={updateGrowPhase.isPending || setPhaseSensors.isPending}
                  className="text-xs px-2.5 py-1"
                >
                  <CheckIcon className="size-3.5" /> Save
                </Button>
                <button
                  onClick={() => setEditPhaseId(null)}
                  className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 px-2 py-1"
                >
                  <XMarkIcon className="size-3.5" /> Cancel
                </button>
              </div>
            </div>
          );
        })()}
      </Card>

      {/* Live gauges: active grows only. A completed grow's live device reading isn't
          part of its history, and the charts below cover the grow's own period. */}
      {!isActive ? null : grow.device_id == null ? (
        <Card className="mb-4 text-sm text-gray-400 dark:text-gray-500">No device linked to this grow.</Card>
      ) : deviceLoading ? (
        <div className="mb-4"><Spinner /></div>
      ) : device && (
        <Card className="mb-4">
          <div className="flex items-baseline justify-between gap-2 mb-3">
            <h2 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
              Live Sensors — {device.label}
              <button
                onClick={() => {
                  // Editing here always targets whichever level is actually driving
                  // the tiles below (the same phase→grow→device-wide chain used to
                  // compute effectiveSensorAssignment) — not always the grow, since a
                  // phase-level override, when present, is what's really in effect.
                  if (currentPhaseForLiveFilter && currentPhaseForLiveFilter.sensor_mode === "custom") {
                    setEditPhaseId(currentPhaseForLiveFilter.id);
                    setPhaseEditDeviceId(currentPhaseForLiveFilter.device_id?.toString() ?? "");
                    setPhaseSensorError(null);
                    setPhaseEditSensorMode("device_wide");
                    setPhaseEditSensorIds([]);
                    phasePillsRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                  } else {
                    setGrowSensorEdit(true);
                    setGrowSensorEditMode(growSensors?.sensor_mode ?? "device_wide");
                    setGrowSensorEditIds(growSensors?.sensor_ids ?? []);
                    growSensorRowRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                  }
                }}
                className="opacity-40 hover:opacity-100 transition-opacity normal-case tracking-normal font-normal"
                title="Edit sensors to track"
              >
                <PencilIcon className="size-3" />
              </button>
            </h2>
            {effectiveLastSeen && (
              <span className={`text-xs whitespace-nowrap ${liveIsStale ? "text-red-500 dark:text-red-400" : "text-gray-400 dark:text-gray-500"}`}>
                Updated {formatRelative(effectiveLastSeen)}
              </span>
            )}
          </div>
          {currentPhaseForLiveFilter && currentPhaseForLiveFilter.sensor_mode === "custom" && (
            <p className="text-xs text-gray-400 dark:text-gray-500 -mt-2 mb-3">
              Overridden by the current phase (&ldquo;{currentPhaseForLiveFilter.phase_name}&rdquo;) — the pencil above jumps to its editor.
            </p>
          )}
          {effectiveSensorAssignment.mode === "custom" && effectiveSensorAssignment.ids.length === 0 ? (
            // Explicit-zero: a deliberate, valid "tracking nothing" state (the default
            // for every new grow going forward), not an error or a stale device.
            <p className="text-sm text-gray-400 dark:text-gray-500 py-2">
              This grow isn&apos;t tracking any sensors on its device — pick some to see live readings.
            </p>
          ) : (
          <div className="flex flex-wrap gap-4">
            {visibleSensors.map(sensor => {
              const vals = effectiveValues[sensor.sensor_code] ?? {};
              const stale = liveIsStale;
              const tempVal = stale ? null : (vals["temperature"] ?? null);
              const humVal  = stale ? null : (vals["humidity"]    ?? null);
              const co2Val  = stale ? null : (vals["co2"]          ?? null);
              const vpd = (tempVal != null && humVal != null) ? computeVPD(tempVal, humVal) : null;
              const thresholds = device.thresholds;
              const isOrange = sensor.sensor_code === "orange1";
              const sensorName = sensor.display_name || sensor.sensor_name || sensor.sensor_code;
              return (
                <div key={sensor.id} className="w-full">
                  <h3 className="flex items-center gap-2 text-sm font-semibold text-sky-800 dark:text-sky-300 mb-1">
                    <span className={`inline-block w-3 h-3 rounded-full ${isOrange ? "bg-orange-500" : "bg-sky-500"}`} />
                    {sensorName}
                    <span className={`text-xs font-mono font-normal ${isOrange ? "text-orange-400 dark:text-orange-300" : "text-sky-400 dark:text-sky-300"}`}>
                      {sensor.sensor_code}
                    </span>
                  </h3>
                  {stale && (
                    <div className="text-sm text-gray-400 dark:text-gray-500 mb-2">n/a — device hasn't reported recently</div>
                  )}
                  <div className="flex gap-3 flex-wrap">
                  {tempVal != null && (
                    <Gauge
                      title="Temp °F"
                      value={tempVal}
                      limitLow={thresholds["temperature"]?.low_value ?? 60}
                      limitHigh={thresholds["temperature"]?.high_value ?? 90}
                      minValue={40}
                      maxValue={100}
                    />
                  )}
                  {humVal != null && (
                    <Gauge
                      title="Humidity %"
                      value={humVal}
                      limitLow={thresholds["humidity"]?.low_value ?? 60}
                      limitHigh={thresholds["humidity"]?.high_value ?? 95}
                      minValue={0}
                      maxValue={100}
                    />
                  )}
                  {co2Val != null && (
                    <Gauge
                      title="CO₂ ppm"
                      value={co2Val}
                      limitLow={thresholds["co2"]?.low_value ?? 400}
                      limitHigh={thresholds["co2"]?.high_value ?? 2000}
                      minValue={0}
                      maxValue={12000}
                    />
                  )}
                  {vpd != null && (
                    <Gauge
                      title="VPD kPa"
                      value={vpd}
                      limitLow={0.1}
                      limitHigh={0.9}
                      minValue={0}
                      maxValue={2}
                    />
                  )}
                  </div>
                </div>
              );
            })}
          </div>
          )}
        </Card>
      )}

      {/* Charts */}
      {device?.hostname && timeRange && (
        <div className="mb-4">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              Sensor Data — {device.label}
            </h2>
            <div className="flex flex-wrap items-center gap-2">
              {grow.template_grow_id && (
                <GrowReferenceToggle
                  enabled={reference.enabled}
                  onToggle={reference.toggle}
                  isLoading={reference.isLoading}
                  hasError={reference.hasError}
                />
              )}
              <TimeRangePicker
                start={timeRange.start}
                end={timeRange.end}
                onRangeChange={isCompleted ? undefined : handleRangeChange}
                dark={darkMode}
                rangeStartMs={new Date(grow.started_at).getTime()}
                constraintNote={
                  isCompleted
                    ? "Ranges are clamped to this grow — no earlier than the start date and no later than the end date."
                    : "Ranges are clamped to this grow: the start can’t go earlier than when the grow started, and the end tracks the current time — so the “Custom” window keeps growing as the grow runs."
                }
              />
            </div>
          </div>
          {/* Event type toggle chips */}
          {presentTypes.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {presentTypes.map((type) => {
                const meta = EVENT_META[type];
                const active = enabledSet.has(type);
                return (
                  <button
                    key={type}
                    onClick={() => toggleType(type)}
                    className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-all ${
                      active
                        ? "text-white border-transparent"
                        : "bg-white dark:bg-gray-700 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-600 opacity-50"
                    }`}
                    style={active ? { backgroundColor: meta.color, borderColor: meta.color } : {}}
                  >
                    {meta.icon} {meta.label}
                  </button>
                );
              })}
            </div>
          )}

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <MetricChart
              title="Temperature"
              metric="temperature"
              hostname={device.hostname}
              unit="°F"
              color="#f97316"
              timeRange={timeRange}
              marks={marks}
              chartGroup="grow-detail"
              sensorLabels={device.sensorLabels}
              sensorCodes={chartSensorCodes}
              extraSeries={reference.referenceSeriesFor("temperature")}
              photoSeries={photoSeries}
              onPhotoClick={openChartPhoto}
              darkMode={darkMode}
              timeZone={tz}
            />
            <MetricChart
              title="Humidity"
              metric="humidity"
              hostname={device.hostname}
              unit="%"
              color="#3b82f6"
              timeRange={timeRange}
              marks={marks}
              chartGroup="grow-detail"
              sensorLabels={device.sensorLabels}
              sensorCodes={chartSensorCodes}
              extraSeries={reference.referenceSeriesFor("humidity")}
              photoSeries={photoSeries}
              onPhotoClick={openChartPhoto}
              darkMode={darkMode}
              timeZone={tz}
            />
            <MetricChart
              title="CO₂"
              metric="co2"
              hostname={device.hostname}
              unit="ppm"
              color="#10b981"
              timeRange={timeRange}
              marks={marks}
              chartGroup="grow-detail"
              sensorLabels={device.sensorLabels}
              sensorCodes={chartSensorCodes}
              extraSeries={reference.referenceSeriesFor("co2")}
              photoSeries={photoSeries}
              onPhotoClick={openChartPhoto}
              darkMode={darkMode}
              timeZone={tz}
            />
            <VPDChart
              hostname={device.hostname}
              sensorCodes={chartSensorCodes}
              timeRange={timeRange}
              marks={marks}
              darkMode={darkMode}
              timeZone={tz}
            />
          </div>
        </div>
      )}

      {/* Info panel */}
      <Card>
        <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4">Details</h2>
        <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4 text-sm">
          <div>
            <dt className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">Started</dt>
            <dd className="text-gray-700 dark:text-gray-200 font-medium flex items-center gap-1.5">
              {startedAtEdit ? (
                <span className="flex items-center gap-1.5">
                  <Input
                    type="date"
                    size="sm"
                    value={startedAtValue}
                    onChange={e => setStartedAtValue(e.target.value)}
                    className="py-0.5"
                  />
                  <button onClick={handleSaveStartedAt} className="text-sky-600 dark:text-sky-400 hover:text-sky-800 dark:hover:text-sky-300"><CheckIcon className="size-4" /></button>
                  <button onClick={() => { setStartedAtEdit(false); setStartedAtError(null); }} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"><XMarkIcon className="size-4" /></button>
                  {startedAtError && <span className="text-xs text-red-600 dark:text-red-400">{startedAtError}</span>}
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  {formatDate(grow.started_at, tz)}
                  <button
                    onClick={() => {
                      setStartedAtEdit(true);
                      setStartedAtValue(new Date(grow.started_at).toISOString().slice(0, 10));
                    }}
                    className="opacity-40 hover:opacity-100 transition-opacity ml-1"
                  >
                    <PencilIcon className="size-3.5" />
                  </button>
                </span>
              )}
            </dd>
          </div>
          {grow.ended_at && (
            <div>
              <dt className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">Ended</dt>
              <dd className="text-gray-700 dark:text-gray-200 font-medium">{formatDate(grow.ended_at, tz)}</dd>
            </div>
          )}
          <div>
            <dt className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">Species</dt>
            <dd className="text-gray-700 dark:text-gray-200 font-medium">
              <InlineEdit
                value={grow.species ?? ""}
                placeholder="Add species"
                onSave={(species) => updateGrow.mutate({ species })}
                className="text-sm text-gray-700 dark:text-gray-200 font-medium"
              />
            </dd>
          </div>
          <div>
            <dt className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">Substrate</dt>
            <dd className="text-gray-700 dark:text-gray-200 font-medium">
              <InlineEdit
                value={grow.substrate ?? ""}
                placeholder="Add substrate"
                onSave={(substrate) => updateGrow.mutate({ substrate })}
                className="text-sm text-gray-700 dark:text-gray-200 font-medium"
              />
            </dd>
          </div>
          <div>
            <dt className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">Crop type</dt>
            <dd className="text-gray-700 dark:text-gray-200 font-medium capitalize">{grow.crop_type}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">Device</dt>
            <dd className="text-gray-700 dark:text-gray-200 font-medium">
              {deviceEdit ? (
                <span className="flex flex-wrap items-center gap-1.5">
                  <Select
                    size="sm"
                    value={deviceValue}
                    onChange={e => setDeviceValue(e.target.value)}
                    className="py-0.5"
                  >
                    <option value="">No device</option>
                    {teamDevices.map(d => (
                      <option key={d.id} value={d.id}>{d.label}</option>
                    ))}
                  </Select>
                  <button onClick={handleSaveDevice} className="text-sky-600 dark:text-sky-400 hover:text-sky-800 dark:hover:text-sky-300"><CheckIcon className="size-4" /></button>
                  <button onClick={() => { setDeviceEdit(false); setDeviceError(null); }} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"><XMarkIcon className="size-4" /></button>
                  {deviceError && <span className="text-xs text-red-600 dark:text-red-400">{deviceError}</span>}
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  {device ? (
                    <Link
                      to={`/dashboard/${grow.device_id}`}
                      className="text-sky-700 dark:text-sky-400 hover:text-sky-800 dark:hover:text-sky-300 font-medium"
                    >
                      {device.label} →
                    </Link>
                  ) : (
                    <span className="text-gray-400 dark:text-gray-500">No device</span>
                  )}
                  <button
                    onClick={() => {
                      setDeviceEdit(true);
                      setDeviceValue(grow.device_id != null ? String(grow.device_id) : "");
                    }}
                    className="opacity-40 hover:opacity-100 transition-opacity ml-1"
                  >
                    <PencilIcon className="size-3.5" />
                  </button>
                </span>
              )}
            </dd>
          </div>
          {grow.device_id != null && (
            <div ref={growSensorRowRef} className="sm:col-span-2">
              <dt className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">Sensors to track</dt>
              <dd className="text-gray-700 dark:text-gray-200 font-medium">
                {growSensorEdit ? (
                  <span className="flex flex-col gap-1.5">
                    <SensorAssignmentPicker
                      sensors={device?.sensors ?? []}
                      sensorMode={growSensorEditMode}
                      sensorIds={growSensorEditIds}
                      onChange={({ sensor_mode, sensor_ids }) => { setGrowSensorEditMode(sensor_mode); setGrowSensorEditIds(sensor_ids); }}
                    />
                    <span className="flex items-center gap-1.5">
                      <button onClick={handleSaveGrowSensors} className="text-sky-600 dark:text-sky-400 hover:text-sky-800 dark:hover:text-sky-300"><CheckIcon className="size-4" /></button>
                      <button onClick={() => { setGrowSensorEdit(false); setGrowSensorError(null); }} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"><XMarkIcon className="size-4" /></button>
                      {growSensorError && <span className="text-xs text-red-600 dark:text-red-400">{growSensorError}</span>}
                    </span>
                  </span>
                ) : (
                  <span className="flex items-center gap-1">
                    {growSensors?.sensor_mode === "custom" ? (
                      growSensors.sensor_ids?.length > 0 ? (
                        <span>
                          {growSensors.sensor_ids
                            .map(id => device?.sensors?.find(s => s.id === id))
                            .filter(Boolean)
                            .map(s => s.display_name || s.sensor_name || s.sensor_code)
                            .join(", ")}
                        </span>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500">Tracking no sensors</span>
                      )
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500">All sensors on this device</span>
                    )}
                    <button
                      onClick={() => {
                        setGrowSensorEdit(true);
                        setGrowSensorEditMode(growSensors?.sensor_mode ?? "device_wide");
                        setGrowSensorEditIds(growSensors?.sensor_ids ?? []);
                      }}
                      className="opacity-40 hover:opacity-100 transition-opacity ml-1"
                    >
                      <PencilIcon className="size-3.5" />
                    </button>
                  </span>
                )}
              </dd>
            </div>
          )}
          <div>
            <dt className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">Current phase</dt>
            <dd className="text-gray-700 dark:text-gray-200 font-medium">{currentPhase?.phase_name ?? "—"}</dd>
          </div>
        </dl>
      </Card>

      {/* Phase Sensor Summaries — grouped by phase, then by sensor within each phase */}
      {phaseSummaries.length > 0 && (() => {
        const byPhase = new Map();
        for (const s of phaseSummaries) {
          if (!byPhase.has(s.phase_id)) byPhase.set(s.phase_id, []);
          byPhase.get(s.phase_id).push(s);
        }
        const phaseGroups = [...byPhase.entries()].sort(([a], [b]) => {
          const pa = phases.find(p => p.id === a)?.phase_order ?? 0;
          const pb = phases.find(p => p.id === b)?.phase_order ?? 0;
          return pa - pb;
        });
        const hasMultiSensorDevice = device?.sensors?.length > 1;

        return (
          <Card className="mt-4">
            <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Phase Sensor Averages</h2>
            {hasMultiSensorDevice && (
              <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">
                Showing all sensors on this grow's device.
              </p>
            )}
            <div className="space-y-5">
              {phaseGroups.map(([phaseId, rows]) => {
                const phase = phases.find(p => p.id === phaseId);
                const days = phase
                  ? Math.floor(differenceInSeconds(phase.ended_at ? new Date(phase.ended_at) : Date.now(), new Date(phase.started_at)) / 86400) + 1
                  : null;
                return (
                  <div key={phaseId}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-1.5 font-medium text-gray-700 dark:text-gray-200 text-sm">
                        <span className={`w-2 h-2 rounded-sm shrink-0 ${PHASE_COLORS[phase?.phase_name] ?? "bg-gray-300"}`} />
                        {phase?.phase_name ?? `Phase ${phaseId}`}
                      </div>
                      <span className="text-xs text-gray-400 dark:text-gray-500 tabular-nums">
                        {days != null ? `${days} day${days === 1 ? "" : "s"}` : "—"}
                      </span>
                    </div>
                    <div className="pl-3.5 space-y-3 border-l-2 border-gray-100 dark:border-gray-700">
                      {rows.map(s => (
                        <SensorSummaryRow key={s.sensor_id ?? "legacy"} s={s} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        );
      })()}

      {/* Harvest Log */}
      <div className="mt-4">
        <HarvestLog growId={growId} harvests={harvests} />
      </div>

      {/* Photos */}
      <div className="mt-4">
        <PhotoGallery resource="grows" id={growId} />
      </div>

      {/* Notes & Events */}
      <div className="mt-4">
        <GrowNotesFeed growId={growId} notes={notes} photos={photos} />
      </div>

      {/* Lightbox for photos opened from the chart thumbnails */}
      <Lightbox photo={chartPhoto} onClose={() => setChartPhoto(null)} />
    </LayoutWide>
  );
}
